import axios from 'axios';
import Cookies from 'js-cookie';
import { httpErrorMessage } from './httpErrors';

/**
 * The URL for an uploaded file.
 *
 * Case photos, x-rays and STLs are no longer served straight off the
 * webserver - the API checks the caller against the case before handing
 * one over - so a file request has to carry the token like any other.
 * An <img> tag cannot send an Authorization header, so for those the
 * token goes in the query string, which the API accepts on this one
 * route and nowhere else.
 */
export const fileUrl = (path) => {
    const name = String(path || '').split('/').pop();
    const token = Cookies.get('token');
    const base = `${process.env.REACT_APP_API_BASE_URL}/image-proxy/${name}`;

    return token ? `${base}?token=${encodeURIComponent(token)}` : base;
};

let defaultHeader = {
    "Access-Control-Allow-Origin": "*",
    "Content-Type": "application/json",
}


function getAxios(tocInstance, header) {
    const instance = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        headers: header ? header : defaultHeader
    });

    if (tocInstance) {
        const bearer = Cookies.get("token");
        if (bearer) {
            instance.defaults.headers.common.Authorization = `Bearer ${bearer}`;
        } else {
            localStorage.clear();
            Cookies.remove("token");
            window.location.href = "/";
            // push toast message
            return;
        }
    }
    return instance;
}

function onAxiosRejected(error) {
    // A genuine HTTP/network failure (webserver-level 413/500, a 404 on a
    // bad route, a timeout, CORS...) never goes through this app's own
    // {msg, code, data} envelope — those are only returned by the Laravel
    // controllers themselves. Without normalizing here, every caller's
    // "onError(data, res) => res?.msg" pattern silently finds nothing
    // (the raw axios error has no top-level .msg) and shows no feedback
    // at all, which is exactly why an oversized upload rejected by
    // PHP's post_max_size/upload_max_filesize looked like nothing
    // happened on click.
    const status = error.response ? error.response.status : 0;

    // Only the app's own `msg` is written for a reader. Laravel's own
    // `message` ("Too Many Attempts.") and axios' `error.message`
    // ("Request failed with status code 429") are not, so neither is
    // shown — the status decides the wording instead. See httpErrors.js.
    const msg = (error.response && error.response.data && error.response.data.msg)
        || httpErrorMessage(status, error.response);

    return {
        code: status,
        msg,
        data: error.response ? error.response.data : null,
    };
}

export const get = (uri, payload = null, tocInstance = true, req = false) =>
    getAxios(tocInstance, req)
        .get(uri, { params: payload })
        .then((res) => {
            return res.data;
        }, onAxiosRejected);


export const post = (uri, payload, tocInstance = true, req = false) =>
    getAxios(tocInstance, req)
        .post(uri, payload)
        .then((res) => {
            return res.data;
        }, onAxiosRejected);

export const postFd = (uri, payload, tocInstance = true) => {
    let header = { "Access-Control-Allow-Origin": "*", 'content-type': 'multipart/form-data' }
    return getAxios(tocInstance, header)
        .post(uri, payload)
        .then((res) => {
            return res.data;
        }, onAxiosRejected);
}
const headerWithFormData = {
    ...defaultHeader,
    "Content-Type": "multipart/form-data",
};


export const putFd = (uri, payload, tocInstance = true) =>
    getAxios(tocInstance, headerWithFormData)
        .put(uri, payload)
        .then((res) => {
            return res.data;
        }, onAxiosRejected);


export const put = (uri, payload, tocInstance = true, req = false) =>
    getAxios(tocInstance, req)
        .put(uri, payload)
        .then((res) => {
            return res.data;
        }, onAxiosRejected);


/**
 * A download that comes back as a file rather than as the app's JSON
 * envelope - the case file bundle, for one. Goes through the same
 * instance as everything else, so it carries the token in the header.
 */
export const getBlob = (uri, payload = null) =>
    getAxios(true)
        .get(uri, { params: payload, responseType: 'blob' })
        .then((res) => ({ blob: res.data, headers: res.headers }), onAxiosRejected);


export const del = (uri, tocInstance = true, req = false) =>
    getAxios(tocInstance, req)
        .delete(uri)
        .then((res) => {
            return res.data;
        }, onAxiosRejected);

export const delWithPayload = (uri, payload, tocInstance = true, req = false) =>
    getAxios(tocInstance, req)
        .delete(uri, { data: payload })
        .then((res) => {
            return res.data;
        }, onAxiosRejected);
